import { useState } from 'react';
import { CommentOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Checkbox, Input, Modal, Space, Tooltip, Typography } from 'antd';
import { api } from '../api';

/**
 * Põe questões na lista de trabalho do Comentário Assistido.
 *
 * **Escolher as abas é obrigatório, e é por isso que existe um diálogo.** O
 * backend recusa lista vazia pela mesma razão que o Cadastro Assistido recusa:
 * campo que volta preenchido sem ninguém ter pedido é trabalho que ninguém
 * revisou. Um botão que enfileirasse "tudo" decidiria isso no lugar de quem
 * revisa - e o que se pede muda por prova, porque nem toda questão precisa de
 * doutrina.
 *
 * **Enfileirar não aciona nada.** A questão entra `pendente` e fica lá até o
 * Claude Code pedir trabalho pelo MCP (D-24). Era assim mesmo quando havia dois
 * motores, e continua sendo com um só: quem decide que uma questão vai ser
 * comentada é quem revisa, e quem executa é quem abre o terminal.
 *
 * **A ressalva é opcional, e o campo é onde o retrabalho ganha direção** (D-41).
 * Recomentar uma questão é enfileirá-la de novo, então este diálogo é o momento
 * em que quem revisou sabe dizer o que faltou na tentativa anterior - e sem um
 * lugar para dizê-lo, a próxima rodada repetiria a anterior com outro motor.
 */

/** As abas que a geração escreve, com o nome que a tela usa. */
const ABAS = [
  { valor: 'commentary', rotulo: 'Comentário de cada alternativa' },
  { valor: 'legislation', rotulo: 'Legislação' },
  { valor: 'jurisprudence', rotulo: 'Jurisprudência' },
  { valor: 'doctrine', rotulo: 'Doutrina' },
] as const;

const PADRAO = ABAS.map((aba) => aba.valor as string);

interface Props {
  questionIds: string[];
  /** Botão discreto na questão aberta, destacado na seleção em massa. */
  size?: 'small' | 'middle';
  /**
   * Só o ícone, para telas estreitas.
   *
   * No cabeçalho da questão são quatro alvos ao lado do menu, e o rótulo por
   * extenso empurra a linha num aparelho estreito. O `title` carrega o
   * significado, e o diálogo que abre já diz o que vai acontecer.
   */
  compacto?: boolean;
  onEnfileirado?: () => void;
}

export function EnfileirarComentario({
  questionIds,
  size = 'small',
  compacto = false,
  onEnfileirado,
}: Props) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [abas, setAbas] = useState<string[]>(PADRAO);
  const [briefing, setBriefing] = useState('');

  const enfileirar = useMutation({
    mutationFn: () => api.enfileirarComentarios(questionIds, abas, briefing),
    onSuccess: async (resultado) => {
      /*
       * O que já estava na lista é dito, e não silenciado: quem seleciona
       * cinquenta questões e vê "12 enfileiradas" precisa saber que as outras
       * trinta e oito já estavam lá, e não que algo falhou.
       */
      const jaEstavam = resultado.jaNaLista.length;
      message.success(
        jaEstavam > 0
          ? `${resultado.enfileiradas} na lista. ${jaEstavam} já estavam.`
          : `${resultado.enfileiradas} na lista de trabalho.`,
      );

      /*
       * **A ressalva que não alcançou é dita, e não engolida.** Questão que já
       * está com um motor não recebe a crítica: aquele trabalho levou o pacote
       * sem ela. Calar isso deixaria quem escreveu supor que alcançou todas - e
       * a ressalva existe justamente para mudar o resultado.
       */
      if (resultado.ressalvaNaoAlcancou > 0) {
        message.warning(
          resultado.ressalvaNaoAlcancou === 1
            ? 'A ressalva não alcançou 1 questão que já está com um motor.'
            : `A ressalva não alcançou ${resultado.ressalvaNaoAlcancou} questões que já estão com um motor.`,
        );
      }
      setAberto(false);
      // A ressalva é daquele lote: mantida, reapareceria no próximo diálogo e
      // seria enviada de novo para questões sobre as quais ninguém a escreveu.
      setBriefing('');
      await queryClient.invalidateQueries({ queryKey: ['comment-jobs'] });
      onEnfileirado?.();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  return (
    <>
      <Tooltip title={compacto ? 'Comentário Assistido' : undefined}>
        <Button
          size={size}
          icon={<CommentOutlined />}
          /*
           * **A ressalva zera ao abrir, e não só ao enviar.** O componente é o
           * mesmo enquanto a seleção muda na tela de questões: quem escreve uma
           * ressalva, cancela e reabre com outras questões selecionadas mandaria
           * a crítica de um lote para outro - silenciosamente, porque o texto
           * continuaria na caixa e pareceria intencional.
           */
          onClick={() => {
            setBriefing('');
            setAberto(true);
          }}
          disabled={questionIds.length === 0}
          aria-label="Comentário Assistido"
        >
          {compacto ? null : 'Comentário Assistido'}
        </Button>
      </Tooltip>

      <Modal
        open={aberto}
        title={
          questionIds.length === 1
            ? 'Enfileirar esta questão'
            : `Enfileirar ${questionIds.length} questões`
        }
        okText="Enfileirar"
        cancelText="Cancelar"
        confirmLoading={enfileirar.isPending}
        okButtonProps={{ disabled: abas.length === 0 }}
        onOk={() => enfileirar.mutate()}
        onCancel={() => setAberto(false)}
      >
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            O que a geração deve escrever. Aba não pedida não é tocada, e o que já
            estiver escrito permanece.
          </Typography.Text>

          <Checkbox.Group
            value={abas}
            onChange={(valores) => setAbas(valores as string[])}
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            options={ABAS.map((aba) => ({ label: aba.rotulo, value: aba.valor }))}
          />

          <div>
            <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
              Ressalva para quem for comentar{' '}
              <Typography.Text type="secondary" style={{ fontWeight: 'normal' }}>
                (opcional)
              </Typography.Text>
            </Typography.Text>
            <Input.TextArea
              value={briefing}
              onChange={(evento) => setBriefing(evento.target.value)}
              rows={3}
              maxLength={4000}
              showCount
              placeholder="O que faltou na tentativa anterior. Ex.: a aba de Doutrina ficou rasa; falta distinguir o art. 1.539 do art. 1.540."
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Vale para {questionIds.length === 1 ? 'esta questão' : 'todas as questões'} deste
              envio.
            </Typography.Text>
          </div>

          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Entrar na lista não aciona nada: a questão fica disponível até o Claude
            Code pedir trabalho pelo MCP.
          </Typography.Text>
        </Space>
      </Modal>
    </>
  );
}
